'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Send, Clock, CheckCircle2, XCircle, AlertTriangle, RefreshCw, RotateCcw, ChevronDown, ChevronUp, Activity, Inbox, Zap,  } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { campaignService, type Campaign } from '@/lib/services/mailflowService';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface DeliveryRecord {
  id: string;
  name: string;
  subject: string;
  listName: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'bounced';
  sentCount: number;
  totalCount: number;
  delivered: number;
  bounces: number;
  failures: number;
  openRate: string;
  scheduledAt: string | null;
  createdAt: string;
  retryCount: number;
  lastRetry: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDeliveryRecord(c: Campaign): DeliveryRecord {
  const total = c.totalCount || 100;
  const sent = c.sentCount || 0;
  const bounces = Math.floor(total * 0.02);
  const failures = Math.floor(total * 0.01);
  const delivered = Math.max(0, sent - bounces - failures);
  return {
    id: c.id,
    name: c.name,
    subject: c.subject,
    listName: c.listName,
    status: c.status === 'scheduled' ? 'pending' : (c.status as DeliveryRecord['status']),
    sentCount: sent,
    totalCount: total,
    delivered,
    bounces,
    failures,
    openRate: c.openRate,
    scheduledAt: c.scheduledAt,
    createdAt: c.createdAt,
    retryCount: 0,
    lastRetry: null,
  };
}

function progressPct(r: DeliveryRecord) {
  if (r.totalCount === 0) return 0;
  return Math.min(100, Math.round((r.sentCount / r.totalCount) * 100));
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({
  icon: Icon, label, value, sub, color,
}: {
  icon: React.ElementType; label: string; value: string | number; sub?: string; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-extrabold text-foreground leading-none">{value}</p>
        <p className="text-xs font-semibold text-muted-foreground mt-0.5">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ProgressBar({ pct, status }: { pct: number; status: DeliveryRecord['status'] }) {
  const color =
    status === 'sending' ? '#5b35e8' :
    status === 'sent' ? '#22c55e' :
    status === 'failed' ? '#ef4444' :
    status === 'bounced'? '#f97316' : '#94a3b8';
  return (
    <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${pct}%`, background: color }}
      />
    </div>
  );
}

const statusBadge: Record<DeliveryRecord['status'], 'warning' | 'primary' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  sending: 'primary',
  sent: 'success',
  failed: 'danger',
  bounced: 'neutral',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DeliveryMonitorClient() {
  const [records, setRecords] = useState<DeliveryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<'all' | DeliveryRecord['status']>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const campaigns = await campaignService.getAll();
      setRecords(campaigns.map(toDeliveryRecord));
    } catch {
      toast.error('Failed to load delivery data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s for live sending campaigns
    const interval = setInterval(() => load(true), 30000);
    return () => clearInterval(interval);
  }, [load]);

  const filtered = records.filter((r) => filter === 'all' || r.status === filter);

  const stats = {
    pending: records.filter((r) => r.status === 'pending').length,
    sending: records.filter((r) => r.status === 'sending').length,
    delivered: records.reduce((s, r) => s + r.delivered, 0),
    bounces: records.reduce((s, r) => s + r.bounces, 0),
    failures: records.reduce((s, r) => s + r.failures, 0),
  };

  const handleRetry = async (id: string) => {
    setRetrying((prev) => new Set(prev).add(id));
    await new Promise((res) => setTimeout(res, 1500));
    setRecords((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, status: 'sending', retryCount: r.retryCount + 1, lastRetry: new Date().toISOString() }
          : r
      )
    );
    setRetrying((prev) => { const n = new Set(prev); n.delete(id); return n; });
    toast.success('Campaign queued for retry');
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const filterTabs: { label: string; value: typeof filter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Sending', value: 'sending' },
    { label: 'Sent', value: 'sent' },
    { label: 'Bounced', value: 'bounced' },
    { label: 'Failed', value: 'failed' },
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Delivery Monitor</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Live send progress, delivery status, bounces, and failure retry controls
            </p>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-secondary transition-all"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard icon={Clock} label="Pending" value={stats.pending} sub="Queued campaigns" color="#f59e0b" />
          <StatCard icon={Zap} label="Sending" value={stats.sending} sub="In progress now" color="#5b35e8" />
          <StatCard icon={CheckCircle2} label="Delivered" value={stats.delivered.toLocaleString()} sub="Total emails" color="#22c55e" />
          <StatCard icon={AlertTriangle} label="Bounces" value={stats.bounces.toLocaleString()} sub="Hard + soft" color="#f97316" />
          <StatCard icon={XCircle} label="Failures" value={stats.failures.toLocaleString()} sub="Delivery errors" color="#ef4444" />
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 p-1 bg-secondary rounded-xl w-fit">
          {filterTabs.map((t) => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                filter === t.value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
              {t.value !== 'all' && (
                <span className="ml-1.5 text-xs opacity-60">
                  {records.filter((r) => r.status === t.value).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Campaign List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-secondary rounded w-1/3 mb-3" />
                <div className="h-2 bg-secondary rounded w-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center">
              <Inbox size={24} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-muted-foreground">No campaigns in this category</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((record) => {
              const pct = progressPct(record);
              const isExpanded = expanded.has(record.id);
              const isRetrying = retrying.has(record.id);
              const canRetry = record.status === 'failed' || record.status === 'bounced';

              return (
                <div
                  key={record.id}
                  className="bg-card border border-border rounded-2xl overflow-hidden transition-all"
                >
                  {/* Main row */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="font-bold text-foreground text-sm truncate">{record.name}</span>
                          <Badge variant={statusBadge[record.status]}>
                            {record.status === 'sending' && (
                              <Activity size={10} className="mr-1 animate-pulse" />
                            )}
                            {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                          </Badge>
                          {record.retryCount > 0 && (
                            <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
                              Retry #{record.retryCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{record.subject}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          List: <span className="font-medium text-foreground">{record.listName}</span>
                          {record.scheduledAt && (
                            <> · Scheduled: <span className="font-medium text-foreground">{fmtDate(record.scheduledAt)}</span></>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {canRetry && (
                          <button
                            onClick={() => handleRetry(record.id)}
                            disabled={isRetrying}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border bg-card hover:bg-secondary transition-all disabled:opacity-50"
                          >
                            <RotateCcw size={13} className={isRetrying ? 'animate-spin' : ''} />
                            {isRetrying ? 'Retrying…' : 'Retry'}
                          </button>
                        )}
                        <button
                          onClick={() => toggleExpand(record.id)}
                          className="w-8 h-8 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-secondary transition-all"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                    </div>

                    {/* Progress bar (always visible for sending/pending) */}
                    {(record.status === 'sending' || record.status === 'pending') && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs text-muted-foreground">
                            {record.status === 'sending' ? 'Send progress' : 'Queued'}
                          </span>
                          <span className="text-xs font-bold text-foreground">
                            {record.sentCount.toLocaleString()} / {record.totalCount.toLocaleString()}
                            <span className="text-muted-foreground font-normal ml-1">({pct}%)</span>
                          </span>
                        </div>
                        <ProgressBar pct={pct} status={record.status} />
                      </div>
                    )}
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && (
                    <div className="border-t border-border bg-secondary/30 px-5 py-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Total Recipients</p>
                          <p className="text-sm font-bold text-foreground">{record.totalCount.toLocaleString()}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Delivered</p>
                          <p className="text-sm font-bold" style={{ color: '#22c55e' }}>{record.delivered.toLocaleString()}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Bounces</p>
                          <p className="text-sm font-bold" style={{ color: '#f97316' }}>{record.bounces.toLocaleString()}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Failures</p>
                          <p className="text-sm font-bold" style={{ color: '#ef4444' }}>{record.failures.toLocaleString()}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Open Rate</p>
                          <p className="text-sm font-bold text-foreground">{record.openRate}</p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Delivery Rate</p>
                          <p className="text-sm font-bold text-foreground">
                            {record.sentCount > 0
                              ? `${Math.round((record.delivered / record.sentCount) * 100)}%`
                              : '—'}
                          </p>
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-xs text-muted-foreground">Created</p>
                          <p className="text-sm font-bold text-foreground">{fmtDate(record.createdAt)}</p>
                        </div>
                        {record.lastRetry && (
                          <div className="space-y-0.5">
                            <p className="text-xs text-muted-foreground">Last Retry</p>
                            <p className="text-sm font-bold text-foreground">{fmtDate(record.lastRetry)}</p>
                          </div>
                        )}
                      </div>

                      {/* Delivery breakdown bar */}
                      {record.sentCount > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-muted-foreground mb-2">Delivery breakdown</p>
                          <div className="flex h-2 rounded-full overflow-hidden gap-0.5">
                            <div
                              className="rounded-l-full"
                              style={{
                                width: `${Math.round((record.delivered / record.sentCount) * 100)}%`,
                                background: '#22c55e',
                              }}
                            />
                            <div
                              style={{
                                width: `${Math.round((record.bounces / record.sentCount) * 100)}%`,
                                background: '#f97316',
                              }}
                            />
                            <div
                              className="rounded-r-full"
                              style={{
                                width: `${Math.round((record.failures / record.sentCount) * 100)}%`,
                                background: '#ef4444',
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#22c55e' }} />
                              Delivered
                            </span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#f97316' }} />
                              Bounced
                            </span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#ef4444' }} />
                              Failed
                            </span>
                          </div>
                        </div>
                      )}

                      {canRetry && (
                        <div className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                          <AlertTriangle size={16} style={{ color: '#f97316' }} className="flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-foreground">
                              {record.status === 'failed' ? 'Delivery failed' : 'Bounce detected'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {record.failures} emails failed to deliver.
                              {record.retryCount > 0 ? ` Retried ${record.retryCount} time(s).` : ' Click Retry to re-queue.'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRetry(record.id)}
                            disabled={isRetrying}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all disabled:opacity-50"
                            style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}
                          >
                            <RotateCcw size={12} className={isRetrying ? 'animate-spin' : ''} />
                            {isRetrying ? 'Retrying…' : 'Retry Now'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
